import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';
import { message, Button, Spin } from 'antd';

import {
  getShares, getSuggestions, createShare, revokeShare, selectPartner, clearShareState,
} from '../../actions/shares';
import { hideModal, openModal } from '../../actions/modal';

import InvitationDeliveryBadges from './InvitationDeliveryBadges';
import { canResendInvitation, isActionablePendingInvitation } from './invitationUtils';
import ModalWrapper from './ModalWrapper';
import csrfHeaders from '../../utils/csrfHeaders';
import './shareReferral.scss';

export class ShareReferral extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fileToken: this.props.modalType.data.fileToken,
      pendingInvitation: null,
      pendingInvitationLoading: false,
      inviteActionLoading: false,
      faxDeliveryAvailable: false,
    };
  }

  async componentDidMount() {
    const { fileToken } = this.state;
    await Promise.all([
      this.props.getShares(fileToken),
      this.props.getSuggestions(fileToken),
      this.fetchPendingInvitation(),
    ]);
  }

  componentWillUnmount() {
    this.props.clearShareState();
  }

  handleShare = async () => {
    const { fileToken } = this.state;
    const { selectedPartnerId } = this.props;

    const result = await this.props.createShare(fileToken, selectedPartnerId);
    if (result && result.error) {
      const responseData = result.response?.data || {};
      message.error({ content: responseData.error || 'Failed to share referral. Please try again.' });
      return;
    }

    message.success({ content: 'Referral shared successfully' });
    this.props.hideModal();
    const onShareSuccess = this.props.modalType?.data?.onShareSuccess;
    if (onShareSuccess) onShareSuccess();
  };

  handleRevoke = async () => {
    const { fileToken } = this.state;
    const { activeShare } = this.props;

    const result = await this.props.revokeShare(fileToken, activeShare.id);
    if (result && result.error) {
      message.error({ content: result.response?.data?.error || 'Failed to revoke access. Please try again.' });
      return;
    }

    message.success({ content: 'Share access revoked successfully' });
    await this.props.getShares(fileToken);
    await this.props.getSuggestions(fileToken);
  };

  fetchPendingInvitation = async () => {
    const { fileToken } = this.state;
    if (!fileToken) return;

    this.setState({ pendingInvitationLoading: true });

    try {
      const response = await axios.get('/api/v1/external_partner_invitations/check', {
        params: { record_id: fileToken },
        headers: csrfHeaders(),
      });

      const hasPending = response.data?.record_has_pending_invitation;
      this.setState({
        faxDeliveryAvailable: response.data?.fax_delivery_available === true,
        pendingInvitation: hasPending ? response.data?.existing_invitation || null : null,
      });
    } catch {
      this.setState({ pendingInvitation: null, faxDeliveryAvailable: false });
    } finally {
      this.setState({ pendingInvitationLoading: false });
    }
  };

  handleResendPendingInvitation = async () => {
    const { pendingInvitation } = this.state;
    const canResend = canResendInvitation(pendingInvitation, {
      faxDeliveryAvailable: this.state.faxDeliveryAvailable,
    });
    if (!canResend || !pendingInvitation?.id) return;

    this.setState({ inviteActionLoading: true });
    try {
      const response = await axios.post(
        `/api/v1/external_partner_invitations/${pendingInvitation.id}/resend`,
        {},
        { headers: csrfHeaders() },
      );
      if (response.data?.invitation) {
        this.setState({ pendingInvitation: response.data.invitation });
      }
      if (response.data?.delivery_failed === true) {
        message.error({ content: 'Invitation saved, but delivery failed. Try resending below.' });
      } else {
        message.success({ content: 'Invitation resent successfully' });
      }
    } catch (error) {
      message.error({ content: error.response?.data?.error || 'Failed to resend invitation' });
    } finally {
      this.setState({ inviteActionLoading: false });
    }
  };

  handleRevokePendingInvitation = async () => {
    const { pendingInvitation } = this.state;
    if (!isActionablePendingInvitation(pendingInvitation) || !pendingInvitation?.id) return;

    this.setState({ inviteActionLoading: true });
    try {
      await axios.delete(
        `/api/v1/external_partner_invitations/${pendingInvitation.id}/revoke`,
        { headers: csrfHeaders() },
      );
      message.success({ content: 'Invitation revoked successfully' });
      await this.fetchPendingInvitation();
    } catch (error) {
      message.error({ content: error.response?.data?.error || 'Failed to revoke invitation' });
    } finally {
      this.setState({ inviteActionLoading: false });
    }
  };

  handleSelectPartner = (partnerId) => {
    this.props.selectPartner(partnerId);
  };

  matchBadgeLabel = (matchType) => {
    switch (matchType) {
      case 'double_match':
        return 'NPI & Fax Match';
      case 'npi_only':
        return 'NPI Match';
      case 'fax_only':
        return 'Fax Match';
      default:
        return 'Match';
    }
  };

  renderActiveShare = () => {
    const { activeShare, isLoading } = this.props;

    return (
      <div>
        <div className="share-referral__active-card">
          <div className="share-referral__partner-name">
            {activeShare.external_partner?.company_name || 'Partner'}
          </div>
          <div className="share-referral__partner-email">
            {activeShare.external_partner?.email}
          </div>
          <div className="share-referral__metadata">
            <span>
              Shared by:
              {' '}
              {activeShare.shared_by?.name || 'Unknown'}
            </span>
            <span>
              Shared:
              {' '}
              {activeShare.shared_at ? new Date(activeShare.shared_at).toLocaleDateString() : ''}
            </span>
          </div>
        </div>
        <div className="share-referral__revoke-btn">
          <Button
            onClick={this.handleRevoke}
            loading={isLoading}
          >
            Revoke Access
          </Button>
        </div>
      </div>
    );
  };

  renderSuggestions = () => {
    const { suggestions, selectedPartnerId } = this.props;

    return (
      <div className="share-referral__suggestions-list">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.id}
            className={`share-referral__suggestion-card${selectedPartnerId === suggestion.id ? ' share-referral__suggestion-card--selected' : ''}`}
            onClick={() => this.handleSelectPartner(suggestion.id)}
            role="button"
            tabIndex={0}
            aria-label={`Select ${suggestion.company_name}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleSelectPartner(suggestion.id); } }}
          >
            <div className="share-referral__suggestion-info">
              <div className="share-referral__partner-name">
                {suggestion.company_name}
              </div>
              <div className="share-referral__partner-email">
                {suggestion.email}
              </div>
            </div>
            {suggestion.match_type && (
              <span className="share-referral__match-badge">
                {this.matchBadgeLabel(suggestion.match_type)}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  renderNoMatches = () => {
    return (
      <div className="share-referral__no-matches">
        <div className="share-referral__no-matches-title">
          No matching partners found
        </div>
        <div className="share-referral__no-matches-text">
          No matching partners found for this referral&apos;s NPI or fax number.
          Invite a partner to join the Referral Portal.
        </div>
        {this.renderInviteActions()}
      </div>
    );
  };

  renderInviteActions = () => {
    const {
      pendingInvitation,
      pendingInvitationLoading,
      inviteActionLoading,
      faxDeliveryAvailable,
    } = this.state;
    const actionableInvitation = isActionablePendingInvitation(pendingInvitation);
    const canResend = canResendInvitation(pendingInvitation, { faxDeliveryAvailable });
    const pendingDestination = pendingInvitation?.delivery_method === 'fax'
      ? pendingInvitation?.fax_number
      : pendingInvitation?.email;

    if (pendingInvitationLoading) {
      return (
        <div className="share-referral__pending-invite-loading">
          <Spin size="small" />
        </div>
      );
    }

    if (actionableInvitation) {
      return (
        <div className="share-referral__pending-invite">
          <div className="share-referral__pending-invite-text">
            {`Pending invitation already sent to ${pendingDestination || 'this partner'}.`}
          </div>
          <InvitationDeliveryBadges
            invitation={pendingInvitation}
            className="share-referral__invitation-badges"
          />
          <div className="share-referral__pending-invite-actions">
            {canResend && (
              <Button
                className="share-referral__pending-invite-btn share-referral__pending-invite-btn--resend"
                onClick={this.handleResendPendingInvitation}
                loading={inviteActionLoading}
              >
                Resend invitation
              </Button>
            )}
            <Button
              className="share-referral__pending-invite-btn share-referral__pending-invite-btn--revoke"
              onClick={this.handleRevokePendingInvitation}
              loading={inviteActionLoading}
            >
              Revoke invitation
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="share-referral__invite-btn">
        <Button onClick={this.handleOpenInviteModal} disabled={!this.props.mappingComplete}>
          Invite Partner
        </Button>
      </div>
    );
  };

  renderInviteCta = () => (
    <div className="share-referral__invite-cta">
      <div className="share-referral__invite-cta-text">
        Need to invite a different partner?
      </div>
      {this.renderInviteActions()}
    </div>
  );

  handleOpenInviteModal = () => {
    const { fileToken } = this.state;
    this.props.openModal({
      type: 'INVITE_PARTNER',
      data: { recordId: fileToken },
    });
  };

  render() {
    const {
      hideModal, isLoading, activeShare, suggestions, noMatches, selectedPartnerId, error, mappingComplete, // eslint-disable-line no-shadow
    } = this.props;

    const customContentStyle = {
      width: '420px',
    };

    const hasActiveShare = !!activeShare;
    const hasSuggestions = !hasActiveShare && suggestions.length > 0;
    const showNoMatches = !hasActiveShare && noMatches;
    const sharingDisabled = !mappingComplete;

    let modalTitle = 'Share Referral';
    if (hasActiveShare) {
      modalTitle = 'Currently Shared With';
    }

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={hasSuggestions && !sharingDisabled ? this.handleShare : null}
        actionName={hasSuggestions ? 'Share Referral' : null}
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={hasSuggestions && (sharingDisabled || !selectedPartnerId || isLoading)}
        modalTitle={modalTitle}
        isDraggable
      >
        {sharingDisabled && (
          <div className="share-referral__mapping-warning">
            Referral status mapping is required before sharing with external partners.
            Please contact your admin to set that up.
          </div>
        )}
        {error && (
          <div className="share-referral__error">{error}</div>
        )}
        {isLoading && !activeShare && suggestions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin />
          </div>
        )}
        {hasActiveShare && this.renderActiveShare()}
        {hasSuggestions && this.renderSuggestions()}
        {hasSuggestions && this.renderInviteCta()}
        {showNoMatches && this.renderNoMatches()}
      </ModalWrapper>
    );
  }
}

ShareReferral.defaultProps = {
  activeShare: null,
  selectedPartnerId: null,
  error: null,
  mappingComplete: true,
};

ShareReferral.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  activeShare: PropTypes.object,
  suggestions: PropTypes.array.isRequired,
  noMatches: PropTypes.bool.isRequired,
  selectedPartnerId: PropTypes.number,
  error: PropTypes.string,
  mappingComplete: PropTypes.bool,
  getShares: PropTypes.func.isRequired,
  getSuggestions: PropTypes.func.isRequired,
  createShare: PropTypes.func.isRequired,
  revokeShare: PropTypes.func.isRequired,
  selectPartner: PropTypes.func.isRequired,
  clearShareState: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
};

export default connect((state) => ({
  isLoading: state.shares.isLoading,
  activeShare: state.shares.activeShare,
  suggestions: state.shares.suggestions,
  noMatches: state.shares.noMatches,
  selectedPartnerId: state.shares.selectedPartnerId,
  error: state.shares.error,
  mappingComplete: state.shares.mappingComplete,
}), {
  getShares,
  getSuggestions,
  createShare,
  revokeShare,
  selectPartner,
  clearShareState,
  hideModal,
  openModal,
})(ShareReferral);
